import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import type {
  GetModelStorefrontResponse,
  MediaItemSummary,
  ModelRelationshipStatus,
  ModelSocialLink,
  StorefrontModules,
} from '@fetmode/types';
import { DEFAULT_STOREFRONT_MODULES, DEFAULT_STOREFRONT_PALETTE } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { t } from '@fetmode/i18n';
import { formatUsername } from '@fetmode/utils';
import { Button } from '@fetmode/ui';

import { useAuth, useAuthModal } from '../auth/index.js';
import { useCoinShop } from '../wallet/index.js';
import { ProfilePostsPanel } from '../profile/ProfilePostsPanel.js';
import {
  GRID_PAGE_SIZE,
  ThumbGridPager,
  usePagedItems,
} from '../../shared/components/ThumbGridPager/index.js';
import { CustomVideoRequestModal } from './CustomVideoRequestModal.js';
import { OfflineTipModal } from './OfflineTipModal.js';
import { PrivateShowRfpModal } from './PrivateShowRfpModal.js';
import '../../shared/styles/thumb-grid.css';
import './ShowRoomPage.css';
import './ModelStorefront.css';
import './model-storefront-look.css';
import { Link } from 'react-router-dom';

type StorefrontTab = 'videos' | 'posts' | 'pics' | 'goodies' | 'socials' | 'about' | 'wishlist';
type StorefrontPresence = 'LIVE' | 'WAITING' | 'PAUSED' | 'AWAY' | 'offline';

type SocialAccess = 'open' | { coins: number };

interface ModelSocialListing {
  id: string;
  platform: string;
  handle: string;
  access: SocialAccess;
}

function toSocialListings(links: readonly ModelSocialLink[]): ModelSocialListing[] {
  return links.map((link, index) => ({
    id: `${link.platform}-${String(index)}`,
    platform: link.platform,
    handle: link.url,
    access: link.unlockCoins > 0 ? { coins: link.unlockCoins } : 'open',
  }));
}

function visibleStorefrontTabs(modules: StorefrontModules): StorefrontTab[] {
  const tabs: StorefrontTab[] = ['posts'];
  if (modules.clips) {
    tabs.unshift('videos');
    tabs.push('pics');
  }
  if (modules.fanClub) {
    tabs.push('goodies');
  }
  if (modules.social) {
    tabs.push('socials');
  }
  if (modules.about) {
    tabs.push('about');
  }
  if (modules.wishlist) {
    tabs.push('wishlist');
  }
  return tabs;
}

function isVideoClip(item: MediaItemSummary): boolean {
  return item.durationSeconds !== null && item.durationSeconds > 0;
}

function StorefrontMediaCard({ item }: { item: MediaItemSummary }): ReactElement {
  const isFree = !item.owned && item.priceCoins === 0;
  const priceLabel = item.owned
    ? t('common.videos.owned')
    : isFree
      ? t('common.videos.free')
      : t('common.videos.coins').replace('{{amount}}', String(item.priceCoins));

  return (
    <Link
      to={`/@${item.model.username}/v/${item.slug}`}
      className={`thumb-card ${item.owned || isFree ? 'thumb-card--owned' : 'thumb-card--price'}`}
    >
      <div className={`thumb-card__thumb${item.thumbnailUrl ? '' : ' thumb-card__thumb--empty'}`}>
        {item.thumbnailUrl ? (
          <img src={item.thumbnailUrl} alt="" loading="lazy" width={640} height={360} />
        ) : null}
        <span
          className={`thumb-card__badge ${item.owned || isFree ? 'thumb-card__badge--owned' : 'thumb-card__badge--price'}`}
        >
          {priceLabel}
        </span>
        <div className="thumb-card__meta">
          <h3 className="thumb-card__title">{item.title}</h3>
        </div>
      </div>
    </Link>
  );
}

function PagedStorefrontMediaGrid({
  items,
  emptyLabel,
  pagerLabel,
}: {
  items: readonly MediaItemSummary[];
  emptyLabel: string;
  pagerLabel: string;
}): ReactElement {
  const paging = usePagedItems(items, GRID_PAGE_SIZE);
  if (items.length === 0) {
    return <p className="model-storefront__empty">{emptyLabel}</p>;
  }
  return (
    <>
      <ul className="thumb-grid model-storefront__media">
        {paging.pageItems.map((item) => (
          <li key={item.id}>
            <StorefrontMediaCard item={item} />
          </li>
        ))}
      </ul>
      <ThumbGridPager
        page={paging.page}
        total={paging.total}
        pageSize={GRID_PAGE_SIZE}
        label={pagerLabel}
        onPageChange={paging.setPage}
      />
    </>
  );
}

export interface ModelStorefrontProps {
  username: string;
  presence?: StorefrontPresence;
  canTip?: boolean;
  coinBalance?: number;
  isTipping?: boolean;
  tipError?: string | null;
  onSendTip?: (amountCoins: number, message?: string) => Promise<boolean>;
}

export function ModelStorefront({
  username,
  presence = 'offline',
  canTip = false,
  coinBalance = 0,
  isTipping = false,
  tipError = null,
  onSendTip,
}: ModelStorefrontProps): ReactElement {
  const { api, user } = useAuth();
  const { openAuthModal, requireAuth } = useAuthModal();
  const { openCoinShop, requireCoins, requireSpendCoins } = useCoinShop();
  const [storefront, setStorefront] = useState<GetModelStorefrontResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isJoiningFanClub, setIsJoiningFanClub] = useState(false);
  const [fanClubError, setFanClubError] = useState<string | null>(null);
  const [socialError, setSocialError] = useState<string | null>(null);
  const [relationshipError, setRelationshipError] = useState<string | null>(null);
  const [isUpdatingRelationship, setIsUpdatingRelationship] = useState(false);
  const [boughtSocialIds, setBoughtSocialIds] = useState<ReadonlySet<string>>(() => new Set());
  const [unlockingSocialId, setUnlockingSocialId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<StorefrontTab>('videos');
  const [nextOnlineAt, setNextOnlineAt] = useState<string | null>(null);
  const [rfpOpen, setRfpOpen] = useState(false);
  const [customVideoOpen, setCustomVideoOpen] = useState(false);
  const [offlineTipOpen, setOfflineTipOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setBoughtSocialIds(new Set());
    setRelationshipError(null);

    void Promise.all([
      api.getModelStorefront(username),
      api.listModelSchedule(username).catch(() => null),
    ])
      .then(([result, schedule]) => {
        if (!cancelled) {
          setStorefront(result);
          setNextOnlineAt(schedule?.nextOnlineAt ?? null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t('common.storefront.loadError'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api, username, user?.id]);

  const applyRelationship = (relationship: ModelRelationshipStatus): void => {
    setStorefront((current) => (current ? { ...current, relationship } : current));
  };

  const handleToggleFollow = (): void => {
    requireAuth(() => {
      if (!storefront) {
        return;
      }
      setIsUpdatingRelationship(true);
      setRelationshipError(null);
      const next = storefront.relationship.isFollowing
        ? api.unfollowModel(username)
        : api.followModel(username);

      void next
        .then((result) => {
          applyRelationship(result.relationship);
        })
        .catch((err: unknown) => {
          setRelationshipError(
            err instanceof ApiError ? err.message : t('common.storefront.socialError'),
          );
        })
        .finally(() => {
          setIsUpdatingRelationship(false);
        });
    }, 'follow');
  };

  const handleToggleFavorite = (): void => {
    requireAuth(() => {
      if (!storefront) {
        return;
      }
      setIsUpdatingRelationship(true);
      setRelationshipError(null);
      const next = storefront.relationship.isFavorite
        ? api.unfavoriteModel(username)
        : api.favoriteModel(username);

      void next
        .then((result) => {
          applyRelationship(result.relationship);
        })
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.code === 'FAVORITE_LIMIT_REACHED') {
            setRelationshipError(t('common.storefront.favoriteLimit'));
            return;
          }
          setRelationshipError(
            err instanceof ApiError ? err.message : t('common.storefront.socialError'),
          );
        })
        .finally(() => {
          setIsUpdatingRelationship(false);
        });
    }, 'follow');
  };

  const handleToggleLiveNotify = (): void => {
    requireAuth(() => {
      if (!storefront) {
        return;
      }
      if (!storefront.relationship.isFollowing) {
        setRelationshipError(t('common.storefront.notifyFollowFirst'));
        return;
      }
      const current = storefront.relationship.notifications?.modelLive ?? true;
      setIsUpdatingRelationship(true);
      setRelationshipError(null);
      void api
        .updateModelNotifications(username, { modelLive: !current })
        .then((result) => {
          applyRelationship(result.relationship);
        })
        .catch((err: unknown) => {
          setRelationshipError(
            err instanceof ApiError ? err.message : t('common.storefront.socialError'),
          );
        })
        .finally(() => {
          setIsUpdatingRelationship(false);
        });
    }, 'follow');
  };

  const handleJoinFanClub = (): void => {
    requireAuth(() => {
      const monthlyCoins = storefront?.fanClub.monthlyCoins ?? 0;
      const runJoinFanClub = (): void => {
        setIsJoiningFanClub(true);
        setFanClubError(null);

        void api
          .joinFanClub(username)
          .then((result) => {
            setStorefront((current) =>
              current
                ? {
                    ...current,
                    fanClub: {
                      ...current.fanClub,
                      monthlyCoins: result.monthlyCoins,
                      isMember: true,
                      memberTierId: result.tierId,
                    },
                  }
                : current,
            );
          })
          .catch((err: unknown) => {
            setFanClubError(err instanceof ApiError ? err.message : t('common.storefront.fanClubError'));
          })
          .finally(() => {
            setIsJoiningFanClub(false);
          });
      };
      void requireSpendCoins(monthlyCoins, runJoinFanClub).then((ok) => {
        if (ok) {
          runJoinFanClub();
        }
      });
    }, 'fanClub');
  };

  const isSocialUnlocked = (social: ModelSocialListing): boolean => {
    if (social.access === 'open') {
      return true;
    }
    return boughtSocialIds.has(social.id);
  };

  const handleUnlockSocial = (social: ModelSocialListing): void => {
    requireAuth(() => {
      setSocialError(null);

      if (social.access === 'open') {
        return;
      }

      if (!onSendTip) {
        setSocialError(t('common.storefront.socialUnlockLiveOnly'));
        return;
      }

      if (!requireCoins(coinBalance, social.access.coins, () => {
        handleUnlockSocial(social);
      })) {
        return;
      }

      if (!canTip) {
        setSocialError(t('common.storefront.socialUnlockBalance'));
        return;
      }

      setUnlockingSocialId(social.id);
      void onSendTip(social.access.coins, `${t('common.storefront.socialUnlockTip')} · ${social.platform}`)
        .then((sent) => {
          if (sent) {
            setBoughtSocialIds((current) => new Set([...current, social.id]));
          }
        })
        .finally(() => {
          setUnlockingSocialId(null);
        });
    }, 'tip');
  };

  if (isLoading) {
    return <p className="model-storefront__loading">{t('common.auth.loading')}</p>;
  }

  if (error || !storefront) {
    return <p className="fm-error">{error ?? t('common.show.notFound')}</p>;
  }

  const { profile, offerings, media, fanClub, relationship } = storefront;
  const modules = profile.storefrontModules ?? DEFAULT_STOREFRONT_MODULES;
  const palette = profile.storefrontPalette ?? DEFAULT_STOREFRONT_PALETTE;
  const videos = media.filter(isVideoClip);
  const pics = media.filter((item) => !isVideoClip(item));
  const socials = toSocialListings(profile.socialLinks);
  const allowedTabs = visibleStorefrontTabs(modules);
  const currentTab = allowedTabs.includes(activeTab) ? activeTab : (allowedTabs[0] ?? 'posts');
  const isOwnProfile = Boolean(user?.username && user.username === profile.username);
  const favoriteLabel = relationship.isFavorite
    ? t('common.storefront.removeFromTop10')
    : t('common.storefront.addToTop10');
  const favoriteRankLabel =
    relationship.isFavorite && relationship.favoriteRank !== null
      ? t('common.storefront.inTop10').replace('{{rank}}', String(relationship.favoriteRank))
      : null;
  const liveNotifyOn = relationship.notifications?.modelLive ?? false;

  const presenceLabel =
    presence === 'LIVE'
      ? t('common.storefront.liveStatus')
      : presence === 'WAITING'
        ? t('common.storefront.waitingStatus')
        : presence === 'PAUSED'
          ? t('common.show.onBreak')
          : presence === 'AWAY'
            ? t('common.show.away')
            : t('common.storefront.offlineStatus');

  const statusClass =
    presence === 'LIVE'
      ? 'model-storefront__status model-storefront__status--live'
      : presence === 'WAITING'
        ? 'model-storefront__status model-storefront__status--waiting'
        : presence === 'PAUSED'
          ? 'model-storefront__status model-storefront__status--break'
          : presence === 'AWAY'
            ? 'model-storefront__status model-storefront__status--away'
            : 'model-storefront__status';

  const tabCatalog: ReadonlyArray<{ id: StorefrontTab; label: string }> = [
    { id: 'videos', label: t('common.storefront.tabVideos') },
    { id: 'posts', label: t('common.storefront.tabPosts') },
    { id: 'pics', label: t('common.storefront.tabPics') },
    { id: 'goodies', label: t('common.storefront.tabGoodies') },
    { id: 'socials', label: t('common.storefront.tabSocials') },
    { id: 'about', label: t('common.storefront.about') },
    { id: 'wishlist', label: t('common.studio.settings.moduleWishlist') },
  ];
  const tabs = tabCatalog.filter((tab) => allowedTabs.includes(tab.id));

  return (
    <div className="model-storefront" data-storefront-palette={palette}>
      <aside className="model-storefront__profile-col">
        <div className="model-storefront__hero">
          <div
            className="model-storefront__cover"
            style={
              profile.coverUrl ? { backgroundImage: `url(${profile.coverUrl})` } : undefined
            }
          />
          <div className="model-storefront__hero-row">
            <div
              className="model-storefront__avatar"
              style={
                profile.avatarUrl ? { backgroundImage: `url(${profile.avatarUrl})` } : undefined
              }
              aria-hidden
            />
          </div>
        </div>
        <div className="model-storefront__identity">
          <h2 className="model-storefront__title">{profile.displayName}</h2>
          <p className="model-storefront__tagline">
            <span className="model-storefront__username">{formatUsername(profile.username)}</span>
          </p>
          <p className={statusClass}>{presenceLabel}</p>
          {modules.schedule && presence === 'offline' && nextOnlineAt ? (
            <p style={{ margin: '0.35rem 0 0', fontSize: '0.875rem', color: 'var(--fm-color-text-muted)' }}>
              {t('common.storefront.nextOnline').replace(
                '{{when}}',
                new Date(nextOnlineAt).toLocaleString(),
              )}
            </p>
          ) : null}
          {modules.schedule && profile.scheduleNote ? (
            <p style={{ margin: '0.35rem 0 0', fontSize: '0.875rem' }}>
              {t('common.storefront.scheduleNoteLabel')}: {profile.scheduleNote}
            </p>
          ) : null}
        </div>

        {!isOwnProfile ? (
          <div className="model-storefront__actions">
            {user ? (
              <>
                <Button
                  label={
                    relationship.isFollowing
                      ? t('common.storefront.unfollow')
                      : t('common.storefront.follow')
                  }
                  variant={relationship.isFollowing ? 'ghost' : 'secondary'}
                  disabled={isUpdatingRelationship}
                  onClick={handleToggleFollow}
                />
                {favoriteRankLabel ? (
                  <p className="model-storefront__fave-rank">{favoriteRankLabel}</p>
                ) : null}
                <Button
                  label={favoriteLabel}
                  variant={relationship.isFavorite ? 'ghost' : 'secondary'}
                  disabled={isUpdatingRelationship}
                  onClick={handleToggleFavorite}
                />
                {relationship.isFollowing ? (
                  <Button
                    label={
                      liveNotifyOn
                        ? t('common.storefront.notifyWhenLiveOn')
                        : t('common.storefront.notifyWhenLive')
                    }
                    variant={liveNotifyOn ? 'ghost' : 'secondary'}
                    disabled={isUpdatingRelationship}
                    onClick={handleToggleLiveNotify}
                  />
                ) : null}
                <Link to="/settings?tab=notifications" className="model-storefront__text-link">
                  {t('common.storefront.manageNotifications')}
                </Link>
                <Button
                  label={
                    presence === 'offline'
                      ? t('common.storefront.requestScheduledPrivate')
                      : t('common.storefront.requestPrivateShow')
                  }
                  variant="request"
                  onClick={() => {
                    setRfpOpen(true);
                  }}
                />
                <Button
                  label={t('common.storefront.requestCustomVideo')}
                  variant="secondary"
                  onClick={() => {
                    setCustomVideoOpen(true);
                  }}
                />
                {presence === 'offline' && onSendTip ? (
                  <Button
                    label={t('common.storefront.offlineTipCta')}
                    variant="tip"
                    onClick={() => {
                      setOfflineTipOpen(true);
                    }}
                  />
                ) : null}
              </>
            ) : (
              <button
                type="button"
                className="model-storefront__text-link"
                onClick={() => {
                  openAuthModal({ mode: 'register', intent: 'follow' });
                }}
              >
                {t('common.storefront.signInToFollow')}
              </button>
            )}
            {relationshipError ? <p className="fm-error">{relationshipError}</p> : null}
            {!user ? (
              <>
                <Button
                  label={
                    presence === 'offline'
                      ? t('common.storefront.requestScheduledPrivate')
                      : t('common.storefront.requestPrivateShow')
                  }
                  variant="request"
                  onClick={() => {
                    requireAuth(() => {
                      setRfpOpen(true);
                    }, 'private');
                  }}
                />
                <Button
                  label={t('common.storefront.requestCustomVideo')}
                  variant="secondary"
                  onClick={() => {
                    requireAuth(() => {
                      setCustomVideoOpen(true);
                    }, 'purchase');
                  }}
                />
                {presence === 'offline' && onSendTip ? (
                  <Button
                    label={t('common.storefront.offlineTipCta')}
                    variant="tip"
                    onClick={() => {
                      requireAuth(() => {
                        setOfflineTipOpen(true);
                      }, 'tip');
                    }}
                  />
                ) : null}
              </>
            ) : null}
            {presence === 'offline' ? (
              <button
                type="button"
                className="model-storefront__text-link"
                onClick={() => {
                  openCoinShop();
                }}
              >
                {t('common.show.getMoreCoins')}
              </button>
            ) : null}
          </div>
        ) : null}
      </aside>

      <div className="model-storefront__content-col">
        <nav className="model-storefront__tabs" aria-label={t('common.storefront.profileNav')}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={currentTab === tab.id}
              className={
                currentTab === tab.id
                  ? 'model-storefront__tab model-storefront__tab--active'
                  : 'model-storefront__tab'
              }
              onClick={() => {
                setActiveTab(tab.id);
              }}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="model-storefront__body" role="tabpanel">
          {currentTab === 'videos' ? (
            <section>
              <PagedStorefrontMediaGrid
                items={videos}
                emptyLabel={t('common.storefront.videosEmpty')}
                pagerLabel={t('common.storefront.tabVideos')}
              />
            </section>
          ) : null}

          {currentTab === 'posts' ? (
            <ProfilePostsPanel username={profile.username} isSelf={isOwnProfile} />
          ) : null}

          {currentTab === 'pics' ? (
            <section>
              <PagedStorefrontMediaGrid
                items={pics}
                emptyLabel={t('common.storefront.picsEmpty')}
                pagerLabel={t('common.storefront.tabPics')}
              />
            </section>
          ) : null}

          {currentTab === 'goodies' ? (
            <section>
              <ul className="model-storefront__offerings model-storefront__offerings--block">
                <li>
                  {t('common.storefront.fanClub').replace(
                    '{{amount}}',
                    String(offerings.fanClubMonthlyCoins),
                  )}
                </li>
                {offerings.privatesEnabled ? (
                  <>
                    {offerings.vipEnabled !== false ? (
                      <li>
                        {offerings.vipPreTipCoins > 0
                          ? t('common.storefront.vipPrivatePretip')
                              .replace('{{amount}}', String(offerings.vipCoinsPerMinute))
                              .replace('{{pretip}}', String(offerings.vipPreTipCoins))
                          : t('common.storefront.vipPrivate').replace(
                              '{{amount}}',
                              String(offerings.vipCoinsPerMinute),
                            )}
                      </li>
                    ) : null}
                    {offerings.exclusiveEnabled !== false ? (
                      <li>
                        {offerings.exclusivePreTipCoins > 0
                          ? t('common.storefront.exclusivePrivatePretip')
                              .replace('{{amount}}', String(offerings.exclusiveCoinsPerMinute))
                              .replace('{{pretip}}', String(offerings.exclusivePreTipCoins))
                          : t('common.storefront.exclusivePrivate').replace(
                              '{{amount}}',
                              String(offerings.exclusiveCoinsPerMinute),
                            )}
                      </li>
                    ) : null}
                  </>
                ) : null}
              </ul>

              {fanClub.isMember ? (
                <p className="model-storefront__fan-club-member">{t('common.show.fanClubMember')}</p>
              ) : user ? (
                <div className="model-storefront__fan-club-action">
                  <Button
                    label={t('common.show.joinFanClub').replace(
                      '{{amount}}',
                      String(fanClub.monthlyCoins),
                    )}
                    variant="secondary"
                    disabled={isJoiningFanClub}
                    onClick={handleJoinFanClub}
                  />
                  {fanClubError ? <p className="fm-error">{fanClubError}</p> : null}
                </div>
              ) : (
                <p className="model-storefront__hint">
                  <button
                    type="button"
                    className="model-storefront__text-link"
                    onClick={() => {
                      openAuthModal({ mode: 'register', intent: 'fanClub' });
                    }}
                  >
                    {t('common.nav.signIn')}
                  </button>
                  {` ${t('common.storefront.signInForFanClub')}`}
                </p>
              )}
            </section>
          ) : null}

          {currentTab === 'socials' ? (
            <section className="model-storefront__socials" aria-label={t('common.storefront.socialsTitle')}>
              {socials.length === 0 ? (
                <p className="model-storefront__empty">{t('common.storefront.socialsEmpty')}</p>
              ) : (
                <ul className="model-storefront__social-list">
                  {socials.map((social) => {
                    const unlocked = isSocialUnlocked(social);
                    const socialCoins = social.access === 'open' ? 0 : social.access.coins;
                    const busy = unlockingSocialId === social.id;

                    return (
                      <li key={social.id} className="model-storefront__social-item">
                        <div className="model-storefront__social-meta">
                          <span className="model-storefront__social-platform">{social.platform}</span>
                          {unlocked ? (
                            <a
                              className="model-storefront__social-handle"
                              href={social.handle}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {social.handle}
                            </a>
                          ) : (
                            <span className="model-storefront__social-locked">
                              {t('common.storefront.socialPrice').replace(
                                '{{amount}}',
                                String(socialCoins),
                              )}
                            </span>
                          )}
                        </div>
                        {unlocked ? (
                          <span className="model-storefront__social-badge">
                            {t('common.storefront.socialUnlocked')}
                          </span>
                        ) : user ? (
                          <Button
                            label={t('common.storefront.socialBuy').replace(
                              '{{amount}}',
                              String(socialCoins),
                            )}
                            variant="chip"
                            disabled={busy || isTipping || !onSendTip}
                            onClick={() => {
                              handleUnlockSocial(social);
                            }}
                          />
                        ) : (
                          <button
                            type="button"
                            className="model-storefront__text-link"
                            onClick={() => {
                              openAuthModal({
                                mode: 'register',
                                intent: 'tip',
                              });
                            }}
                          >
                            {t('common.storefront.socialSignIn')}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {socialError ? <p className="fm-error">{socialError}</p> : null}
            </section>
          ) : null}

          {currentTab === 'wishlist' ? (
            <section>
              <p className="model-storefront__empty">{t('common.storefront.wishlistEmpty')}</p>
            </section>
          ) : null}

          {currentTab === 'about' ? (
            <section>
              {profile.bio ? (
                <p className="model-storefront__bio">{profile.bio}</p>
              ) : (
                <p className="model-storefront__empty">{t('common.storefront.bioEmpty')}</p>
              )}
            </section>
          ) : null}
        </div>
      </div>

      <PrivateShowRfpModal
        isOpen={rfpOpen}
        onClose={() => {
          setRfpOpen(false);
        }}
        modelUsername={profile.username}
        requireSchedule={presence === 'offline'}
        vipEnabled={offerings.vipEnabled}
        exclusiveEnabled={offerings.exclusiveEnabled}
        vipPreTipCoins={offerings.vipPreTipCoins}
        exclusivePreTipCoins={offerings.exclusivePreTipCoins}
      />
      <CustomVideoRequestModal
        isOpen={customVideoOpen}
        onClose={() => {
          setCustomVideoOpen(false);
        }}
        modelUsername={profile.username}
      />
      {onSendTip ? (
        <OfflineTipModal
          isOpen={offlineTipOpen}
          onClose={() => {
            setOfflineTipOpen(false);
          }}
          canTip={canTip}
          coinBalance={coinBalance}
          isTipping={isTipping}
          tipError={tipError}
          onSendTip={onSendTip}
        />
      ) : null}
    </div>
  );
}
